package com.karyadina.service

import com.karyadina.model.*

/**
 * Platform-agnostic interface for image to PDF conversion
 */
interface ImageToPdfService {
    /**
     * Convert a list of images to a PDF document
     */
    suspend fun convertImagesToPdf(
        images: List<ImageItem>,
        config: PdfConfig,
        outputFileName: String
    ): PdfConversionResult
    
    /**
     * Get estimated PDF size before conversion
     */
    fun estimatePdfSize(images: List<ImageItem>, config: PdfConfig): Long
    
    /**
     * Check if images can be converted (format validation, size limits)
     */
    fun validateImages(images: List<ImageItem>): ValidationResult
}

/**
 * Platform-agnostic interface for image resizing
 */
interface ImageResizerService {
    /**
     * Resize an image according to configuration
     */
    suspend fun resizeImage(
        image: ImageItem,
        config: ImageResizeConfig
    ): ImageResizeResult
    
    /**
     * Batch resize multiple images
     */
    suspend fun resizeImages(
        images: List<ImageItem>,
        config: ImageResizeConfig
    ): List<ImageResizeResult>
    
    /**
     * Apply a preset configuration
     */
    fun applyPreset(preset: ResizePreset): ImageResizeConfig
}

/**
 * Platform-agnostic interface for translation
 */
interface TranslatorService {
    /**
     * Perform OCR to extract text from image or PDF
     */
    suspend fun extractText(inputUri: String, inputType: TranslationInputType): OcrResult
    
    /**
     * Translate extracted text
     */
    suspend fun translateText(
        text: String,
        sourceLanguage: Language,
        targetLanguage: Language
    ): TranslationResult
    
    /**
     * Complete translation pipeline: OCR + Translation
     */
    suspend fun translateDocument(request: TranslationRequest): TranslationResult
    
    /**
     * Get available translation languages
     */
    fun getSupportedLanguages(): List<Language>
}

/**
 * Platform-agnostic interface for file operations
 */
interface FileService {
    /**
     * Pick images from device storage
     */
    suspend fun pickImages(maxCount: Int = 10): List<ImageItem>
    
    /**
     * Pick a single image
     */
    suspend fun pickImage(): ImageItem?
    
    /**
     * Take a photo using camera
     */
    suspend fun takePhoto(): ImageItem?
    
    /**
     * Pick a PDF file
     */
    suspend fun pickPdf(): String?
    
    /**
     * Save file to device storage
     */
    suspend fun saveFile(uri: String, fileName: String): Boolean
    
    /**
     * Share file using platform share sheet
     */
    suspend fun shareFile(uri: String, mimeType: String)
    
    /**
     * Get file info
     */
    suspend fun getFileInfo(uri: String): FileInfo?
}

/**
 * Validation result
 */
data class ValidationResult(
    val isValid: Boolean,
    val errors: List<String> = emptyList()
)

/**
 * File information
 */
data class FileInfo(
    val name: String,
    val uri: String,
    val sizeInBytes: Long,
    val mimeType: String
)
